import { gt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo, useState } from 'react'
import { pbDate } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'
import type { BulkMovement, MovementFilter } from '@/types/movement'
import { useMovementBulkLoad } from './useMovementBulkLoad'

export function useMovementData(filter: MovementFilter & { orgSlug?: string; realtime?: boolean }) {
    const [movementCollection] = useStore('movement')
    const { userIds, startDate, endDate, orgSlug, realtime = false } = filter

    const hasUserIds = userIds && userIds.length > 0
    const [mountedAt] = useState(pbDate())
    const { data: bulkMovements = [], isLoading: isBulkLoading } = useMovementBulkLoad({
        orgSlug,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        enabled: hasUserIds,
    })

    const { data: realtimeMovements = [] } = useLiveQuery(
        q => {
            if (!realtime) return undefined
            return q
                .from({ movement: movementCollection })
                .where(({ movement }) => {
                    return gt(movement.timestamp, mountedAt)
                })
                .orderBy(({ movement }) => movement.timestamp, 'asc')
        },
        [mountedAt, realtime]
    )

    const movements = useMemo(() => {
        if (!hasUserIds) return []
        if (!realtime || realtimeMovements.length === 0) return bulkMovements

        const seen = new Set<string>()
        const merged: BulkMovement[] = []

        for (const m of bulkMovements) {
            if (!seen.has(m.id)) {
                seen.add(m.id)
                merged.push(m)
            }
        }
        for (const m of realtimeMovements as BulkMovement[]) {
            if (!seen.has(m.id)) {
                seen.add(m.id)
                merged.push(m)
            }
        }

        return merged
    }, [bulkMovements, realtimeMovements, hasUserIds, realtime])

    return {
        movements,
        isPending: isBulkLoading,
    }
}
